/**
 * DenSwitchBar — chrome that mounts above every Den, providing:
 *   1. "Switch Den" button (opens DenSwitcherModal)
 *   2. Preview banner when ?preview=1 — "you're previewing X with sample data" + commit/cancel
 *   3. Multi-Den tabs when user has 2+ active Dens
 *   4. "Add a second Den" button (Pro+ only) when user has < tier-cap Dens
 *
 * Mounted by DenDispatcher above the resolved Den component.
 *
 * Tier caps: free=1, pro=2, agency=2, enterprise=5. The cap is enforced
 * client-side here AND on the edge function for the commit.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Repeat, Plus, Eye, Check, ArrowLeft } from "lucide-react";
import { DenSwitcherModal } from "./DenSwitcherModal";
import { toast } from "@/hooks/use-toast";
import { getMyTier } from "@/lib/userTier";

interface ActiveDen {
  persona_key: string;
  is_primary: boolean;
}

interface CatalogEntry {
  persona_key: string;
  display_name: string;
  status: string;
}

const TIER_CAP: Record<string, number> = {
  free: 1,
  pro: 2,
  agency: 2,
  enterprise: 5,
};

interface Props {
  /** Currently-rendered persona key (from forcePersona route param or self-mode resolution). */
  currentPersona: string;
}

export function DenSwitchBar({ currentPersona }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { persona: routePersona } = useParams();
  const isPreview = searchParams.get("preview") === "1";
  const isAddMode = searchParams.get("add") === "1";

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"switch" | "add">("switch");
  const [activeDens, setActiveDens] = useState<ActiveDen[]>([]);
  const [tier, setTier] = useState<string>("free");
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const [adRes, catRes] = await Promise.all([
        supabase.from("user_active_dens" as any).select("persona_key, is_primary").eq("user_id", user.id),
        supabase.from("den_catalog" as any).select("persona_key, display_name, status"),
      ]);
      if (!alive) return;
      setActiveDens((adRes.data as unknown as ActiveDen[]) ?? []);
      setTier(await getMyTier());
      setCatalog((catRes.data as unknown as CatalogEntry[]) ?? []);
    })();
    return () => { alive = false; };
  }, [currentPersona]);

  const cap = TIER_CAP[tier] ?? 1;
  const canAddMore = activeDens.length < cap;

  function openSwitch() { setModalMode("switch"); setModalOpen(true); }
  function openAdd() {
    if (!canAddMore) {
      toast({
        title: "Upgrade to add more Dens",
        description: tier === "free"
          ? "Free tier supports 1 Den. Pro and Agency tiers support 2 active Dens — useful for cross-persona work like 'doctor running a sports media site'."
          : `${tier} tier supports up to ${cap} active Dens. Upgrade to Enterprise for up to 5.`,
      });
      return;
    }
    setModalMode("add");
    setModalOpen(true);
  }

  async function commitSwitch() {
    setCommitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Resolve a client_id to attach the new Den to. Prefer a client
      // whose track matches the current persona (so each Den stays bound
      // to its own data layer); fall back to the user's earliest
      // person-type client; if none, create a stub.
      let { data: client } = await supabase
        .from("clients")
        .select("id")
        .eq("user_id", user.id)
        .eq("track", currentPersona)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!client) {
        const { data: anyClient } = await supabase
          .from("clients")
          .select("id")
          .eq("user_id", user.id)
          .eq("client_type", "person")
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();
        if (anyClient) client = anyClient;
      }

      if (!client) {
        const { data: newClient, error: insErr } = await supabase
          .from("clients")
          .insert({
            user_id: user.id,
            brand_name: "My Den",
            track: currentPersona,
          })
          .select("id")
          .maybeSingle();
        if (insErr) throw insErr;
        client = newClient;
      }
      if (!client) throw new Error("Could not resolve client");

      if (isAddMode) {
        // Add as non-primary
        const { error } = await supabase.from("user_active_dens" as any).upsert(
          { user_id: user.id, client_id: client.id, persona_key: currentPersona, is_primary: false },
          { onConflict: "user_id,persona_key" },
        );
        if (error) throw error;
      } else {
        // Switch primary: demote any other primary, upsert this one
        await supabase.from("user_active_dens" as any).update({ is_primary: false }).eq("user_id", user.id);
        const { error } = await supabase.from("user_active_dens" as any).upsert(
          { user_id: user.id, client_id: client.id, persona_key: currentPersona, is_primary: true },
          { onConflict: "user_id,persona_key" },
        );
        if (error) throw error;

        // Update self_identity in profiles too so DenDispatcher self-mode lands here
        const { error: writeError } = await supabase.from("profiles").update({ self_identity: currentPersona }).eq("id", user.id);
        // A failed write must not reach the success message below.
        if (writeError) throw writeError;

        // Update the client's track field
        const { error: writeError2 } = await supabase.from("clients").update({ track: currentPersona }).eq("id", client.id);
        // A failed write must not reach the success message below.
        if (writeError2) throw writeError2;
      }

      toast({ title: "Switched", description: `${currentPersona.replace(/_/g, " ")} Den is now your daily home.` });
      navigate(`/dashboard/dens/${currentPersona}`);
    } catch (e: any) {
      toast({ title: "Switch failed", description: String(e?.message || e), variant: "destructive" });
    } finally {
      setCommitting(false);
    }
  }

  function cancelPreview() { navigate(-1); }

  function switchToDen(personaKey: string) {
    navigate(`/dashboard/dens/${personaKey}`);
  }

  // Display name from catalog
  const currentEntry = catalog.find((c) => c.persona_key === currentPersona);
  const currentName = currentEntry?.display_name || currentPersona.replace(/_/g, " ");

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-2">
        {/* Preview banner */}
        {isPreview && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 flex items-center justify-between gap-3" data-foxchat-id="den-preview-banner">
            <div className="flex items-center gap-2 text-[13px]">
              <Eye className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span>
                <b>Previewing {currentName}</b> with sample data
                {isAddMode ? " (will be added as a second Den)" : ""}.
                Like what you see?
              </span>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button size="sm" variant="ghost" onClick={cancelPreview} disabled={committing}>
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
              <Button size="sm" onClick={commitSwitch} disabled={committing}>
                <Check className="h-3.5 w-3.5 mr-1" />
                {committing ? "Switching…" : isAddMode ? "Add this Den" : "Switch to this Den"}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs + switch chrome */}
        {!isPreview && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-1" data-foxchat-id="den-switch-bar">
            <div className="flex flex-wrap items-center gap-1">
              {/* Multi-Den tabs (only render when user has 2+) */}
              {activeDens.length > 1 ? (
                activeDens.map((d) => {
                  const e = catalog.find((c) => c.persona_key === d.persona_key);
                  const active = d.persona_key === currentPersona;
                  return (
                    <button
                      key={d.persona_key}
                      type="button"
                      onClick={() => switchToDen(d.persona_key)}
                      className={`text-[12px] font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                        active ? "bg-blue-500/15 text-blue-600 border-blue-500/40" : "bg-background border-border/40 text-muted-foreground hover:bg-muted/20"
                      }`}
                    >
                      {e?.display_name || d.persona_key.replace(/_/g, " ")}
                      {d.is_primary && <span className="text-[9px] ml-1.5 px-1 py-0.5 rounded bg-blue-500/20">primary</span>}
                    </button>
                  );
                })
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" onClick={openSwitch}>
                    <Repeat className="h-3.5 w-3.5 mr-1.5" /> Switch Den
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px]">
                  Browse all 27 personas. Preview any built Den with sample data, then commit to make it your daily home.
                </TooltipContent>
              </Tooltip>
              {tier !== "free" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" onClick={openAdd} disabled={!canAddMore}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Den
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[260px]">
                    {canAddMore
                      ? `Add a second Den (you have ${activeDens.length} of ${cap} active).`
                      : `${tier} tier supports up to ${cap} active Dens. Upgrade to add more.`}
                  </TooltipContent>
                </Tooltip>
              )}
              {tier === "free" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="ghost" onClick={openAdd}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Den (Pro)
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[260px]">
                    Pro tier supports 2 active Dens (e.g., a doctor running a sports media site). Upgrade to unlock.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>

      <DenSwitcherModal open={modalOpen} mode={modalMode} onClose={() => setModalOpen(false)} />
    </TooltipProvider>
  );
}

export default DenSwitchBar;
